import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth, formatApiError } from "@/lib/auth";
import { Radar, ArrowRight, Lock } from "lucide-react";

const REMEMBER_KEY = "cr_remembered_email";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/app";

  // On mount, restore remembered email if the user opted in last time.
  useEffect(() => {
    try {
      const saved = localStorage.getItem(REMEMBER_KEY);
      if (saved) {
        setEmail(saved);
        setRemember(true);
      }
    } catch { /* localStorage might be disabled — silently no-op */ }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email, password);
      try {
        if (remember) localStorage.setItem(REMEMBER_KEY, email);
        else localStorage.removeItem(REMEMBER_KEY);
      } catch { /* ignore */ }
      navigate(from, { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] grain flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2.5 mb-8 group" data-testid="login-brand">
          <div className="relative w-9 h-9 border border-[#EAB308] flex items-center justify-center bg-black">
            <Radar className="w-5 h-5 text-[#EAB308]" strokeWidth={2.5} />
          </div>
          <div className="leading-tight">
            <div className="font-heading text-base font-black tracking-tighter uppercase">
              Card<span className="text-[#EAB308]">Radar</span>
            </div>
            <div className="text-[9px] uppercase tracking-[0.25em] text-[#737373] font-mono -mt-0.5">
              TCG Market Scanner
            </div>
          </div>
        </Link>

        <h1 className="font-heading text-3xl font-black tracking-tighter uppercase mb-2">
          Sign in
        </h1>
        <p className="text-sm text-[#A3A3A3] mb-8">
          Welcome back. Time to find some packs.
        </p>

        <form onSubmit={submit} className="space-y-4" data-testid="login-form">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#737373] mb-1.5">Email</div>
            <input
              data-testid="login-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full bg-[#0d0d0d] border border-[#262626] text-white text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-[#EAB308] focus:ring-1 focus:ring-[#EAB308] placeholder:text-[#525252]"
            />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#737373] mb-1.5">Password</div>
            <input
              data-testid="login-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full bg-[#0d0d0d] border border-[#262626] text-white text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-[#EAB308] focus:ring-1 focus:ring-[#EAB308] placeholder:text-[#525252]"
            />
          </div>
          {error && (
            <div data-testid="login-error" className="text-xs text-[#EF4444] bg-[#EF4444]/5 border border-[#EF4444]/30 px-3 py-2 rounded-sm">
              {error}
            </div>
          )}
          <label className="flex items-center gap-2 text-xs text-[#A3A3A3] cursor-pointer select-none">
            <input
              data-testid="login-remember-checkbox"
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              className="accent-[#EAB308] w-3.5 h-3.5"
            />
            Remember my email on this device <span className="text-[#525252]">(password is never saved)</span>
          </label>
          <button
            type="submit"
            data-testid="login-submit-btn"
            disabled={submitting}
            className="w-full bg-[#EAB308] text-black font-bold uppercase tracking-wider text-xs py-3 rounded-sm hover:bg-[#FDE047] transition-all flex items-center justify-center gap-2 disabled:opacity-60 shadow-[0_0_18px_rgba(234,179,8,0.2)]"
          >
            <Lock className="w-4 h-4" />
            {submitting ? "Signing in…" : "Sign in"}
            <ArrowRight className="w-4 h-4" />
          </button>
        </form>

        <p className="text-center text-xs text-[#737373] mt-8">
          No account?{" "}
          <Link to="/register" className="text-[#EAB308] hover:text-[#FDE047] font-bold" data-testid="link-register">
            Create one →
          </Link>
        </p>
      </div>
    </div>
  );
}
